import { useState, useEffect, useMemo, useRef, forwardRef } from "react";
import { B, HD, H, PD } from "../theme";
import { StarField } from "../shared/StarField";
import { BackArrow } from "../shared/BackArrow";
import { Term } from "../shared/Term";
import { PrimaryButton, GhostButton, SKY } from "../shared/ui";
import { WesternGlyph } from "../components/star-charts/ZodiacGlyph";
import { computeNatal } from "../lib/astro/natal";
import { PLANET_LABEL, PLANET_NAME, TRANSIT_TARGET, TRANSIT_VERB, ordinal } from "../lib/astro/labels";
import { BIG_THREE } from "../content/astro/bigThree";
import { ELEMENT_READINGS, LUNATION_READINGS, RETRO_READINGS } from "../content/astro/chartFacts";
import { HOUSE_MEANINGS } from "../content/astro/planetHouse";
import { traditionTiles } from "./traditionTiles";
import { ShareBigThree } from "../share/ShareBigThree";
import { SYSTEMS, NodeGlyph, nodeFacts } from "../chart/ChartMenu";
import { NAKSHATRAS, liChunAnimals } from "../lib/astro/dailyLenses";
import { calculateChineseChart } from "../lib/astro/chinese";
import { calculateMayanChart } from "../lib/astro/mayan";
import { CHINESE_ANIMAL_WRITEUPS } from "../components/star-charts/starChartChineseContent";
import { MAYAN_DAY_SIGN_WRITEUPS, MAYAN_TONE_WRITEUPS } from "../components/star-charts/starChartMayanContent";
import { LittleWeb } from "./FirstQuestions";
import { activeTransits, moonInYourChart, FAST } from "../lib/astro/transits";
import { TRANSIT_FAST, MOON_HOUSE_TODAY, MOON_SIGN_TODAY } from "../content/astro/transitsFast";

/* First-run payoff: a short "reading the sky" beat, then the four
   traditions as equal cards (same order as the Chart menu, none open by
   default), each with one line up front and its depth one tap away. Then the
   save card, which shows the Web's five parts that come next. */

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function birthLine(bd) {
  const { year, month, day } = bd.birthDate;
  const city = (bd.birthLocation?.city || "").split(",")[0];
  return `${MONTHS[month - 1]} ${day}, ${year}${city ? ` · ${city}` : ""}`;
}

function Reading({ birthData }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28, padding: 24, textAlign: "center" }}>
      <svg width="180" height="180" viewBox="-100 -100 200 200" aria-hidden style={{ animation: "twSpin 18s linear infinite" }}>
        <circle r="78" fill="none" stroke={SKY.a} strokeOpacity="0.35" strokeWidth="1" />
        <circle r="62" fill="none" stroke={SKY.b} strokeOpacity="0.2" strokeWidth="1" strokeDasharray="2 6" />
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          return (
            <line key={i} x1={Math.cos(a) * 70} y1={Math.sin(a) * 70} x2={Math.cos(a) * 86} y2={Math.sin(a) * 86}
              stroke={PD.text} strokeOpacity="0.6" strokeWidth="1.2"
              style={{ animation: `twPulse 2.4s ease-in-out ${i * 0.12}s infinite` }} />
          );
        })}
        <circle r="5" fill={PD.text} style={{ animation: "twPulse 1.6s ease-in-out infinite" }} />
      </svg>
      <div>
        <p style={{ ...B, fontSize: 13, fontWeight: 500, color: SKY.a, marginBottom: 10 }}>Reading the sky</p>
        <p style={{ ...H, fontSize: 20, color: PD.text, fontStyle: "italic" }}>{birthLine(birthData)}</p>
      </div>
    </div>
  );
}

const ROLE_LABEL = { sun: "Sun", moon: "Moon", rising: "Rising" };

/** "9th house · beliefs, travel, and higher learning", with the house term explained. */
export function HouseLabel({ house }) {
  if (!house) return null;
  return <><Term k="house">{ordinal(house)} house</Term> · {HOUSE_MEANINGS[house].area}</>;
}

export function BigThreeCard({ role, signKey, title, note, delay, locked, onUnlock }) {
  const [open, setOpen] = useState(false);
  const copy = signKey ? BIG_THREE[role]?.[signKey] : null;
  return (
    <div className="tw-rise" style={{
      animation: `twRise 0.7s ease ${delay}s both`,
      background: "rgba(255,255,255,0.035)", borderRadius: 16, padding: "18px 16px 12px",
    }}>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{ width: 58, height: 58, flexShrink: 0, opacity: locked ? 0.35 : 1 }}>
          {signKey ? <WesternGlyph sign={signKey} size={58} /> : (
            <svg width="58" height="58" viewBox="0 0 58 58" aria-hidden>
              <circle cx="29" cy="29" r="26" fill="none" stroke={PD.borderLit} strokeDasharray="3 5" />
              <text x="29" y="35" textAnchor="middle" fill={PD.sub} fontSize="17">?</text>
            </svg>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ ...B, fontSize: 13, fontWeight: 500, color: SKY.a, marginBottom: 4 }}>
            <Term k={role}>{ROLE_LABEL[role]}</Term>
          </p>
          <p style={{ ...HD, fontSize: 22, color: PD.text, fontWeight: 500, lineHeight: 1.2 }}>{title}</p>
        </div>
      </div>

      {locked ? (
        <div style={{ marginTop: 14 }}>
          <p style={{ ...B, fontSize: 14, color: PD.sub, lineHeight: 1.6 }}>
            Your Rising sign changes about every two hours, so it needs your birth time.
          </p>
          <GhostButton onClick={onUnlock} style={{ paddingLeft: 0, color: SKY.b }}>Add birth time</GhostButton>
        </div>
      ) : copy ? (
        <>
          <p style={{ ...H, fontSize: 19, fontStyle: "italic", color: PD.text, lineHeight: 1.5, marginTop: 16 }}>{copy.line}</p>
          {note && <p style={{ ...B, fontSize: 13, color: PD.sub, lineHeight: 1.55, marginTop: 10 }}>{note}</p>}
          {open && <p style={{ ...B, fontSize: 15, color: PD.sub, lineHeight: 1.7, marginTop: 12, animation: "twFade 0.4s ease both" }}>{copy.more}</p>}
          <GhostButton onClick={() => setOpen(!open)} aria-expanded={open} style={{ paddingLeft: 0, marginTop: 4, color: SKY.b }}>
            {open ? "Less" : "Read more"}
          </GhostButton>
        </>
      ) : null}
    </div>
  );
}

function Fact({ eyebrow, children, delay }) {
  return (
    <div className="tw-rise" style={{ animation: `twRise 0.6s ease ${delay}s both`, padding: "18px 0", borderTop: `1px solid ${PD.border}` }}>
      <p style={{ ...B, fontSize: 13, fontWeight: 500, color: PD.sub, marginBottom: 8 }}>{eyebrow}</p>
      <div style={{ ...B, fontSize: 15, color: PD.text, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

/** Untimed charts: the Rising sign as one row, not a card built around a "?". */
function RisingRow({ onAddTime }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
      padding: "4px 4px 4px 18px", borderRadius: 16, border: `1px dashed ${PD.borderLit}` }}>
      <p style={{ ...B, fontSize: 13, fontWeight: 500, color: SKY.a }}>
        <Term k="rising">Rising sign</Term>
      </p>
      <GhostButton onClick={onAddTime} style={{ color: SKY.b }}>Add birth time</GhostButton>
    </div>
  );
}

/** The strongest fact up front (chartFacts puts the rare ones first), the rest one tap away. */
function ChartFactsShort({ facts }) {
  const [all, setAll] = useState(false);
  if (!facts.length) return null;
  const shown = all ? facts : facts.slice(0, 1);
  return (
    <div style={{ marginTop: 24 }}>
      <p style={{ ...HD, fontSize: 18, color: PD.text, fontWeight: 500, marginBottom: 4 }}>What makes your chart yours</p>
      {shown.map((f, i) => <Fact key={f.key} eyebrow={f.eyebrow} delay={i ? 0 : 0.1}>{f.body}</Fact>)}
      {facts.length > 1 && (
        <GhostButton onClick={() => setAll(!all)} aria-expanded={all} style={{ paddingLeft: 0, color: SKY.b }}>
          {all ? "Show less" : `${facts.length - 1} more about your chart`}
        </GhostButton>
      )}
    </div>
  );
}

/** One line on what today's sky is doing to this chart. Deterministic: a fast
    planet touching a birth placement if there is one, else where the Moon is
    in your houses, else the Moon's sign today (everyone's sky, so `onChart`
    is false). Placements a missing birth time leaves unsure are skipped. */
export function todaySkyLine(natal, now = new Date()) {
  const first = (t) => (t || "").split(/(?<=[.!?])\s/)[0];
  const unsure = (k) => (k === "sun" && natal.sunRange) || (k === "moon" && natal.moonRange);
  try {
    const hit = activeTransits(natal, now).find((t) => FAST.includes(t.body) && !unsure(t.target) && TRANSIT_FAST[t.body]?.[t.target]?.[t.cls]);
    if (hit) {
      // Name the planet and placement first when the line itself doesn't.
      const line = first(TRANSIT_FAST[hit.body][hit.target][hit.cls].text);
      return { onChart: true, text: line.includes(PLANET_NAME[hit.body]) ? line
        : <>{PLANET_NAME[hit.body]} <Term k={hit.type}>{TRANSIT_VERB[hit.type]}</Term> {TRANSIT_TARGET[hit.target]}. {line}</> };
    }
    const m = moonInYourChart(natal, now);
    const houseLine = m.house && MOON_HOUSE_TODAY[m.house];
    if (houseLine) {
      const at = houseLine.indexOf(" house");
      const [pre, post] = [houseLine.slice(0, at), houseLine.slice(at + " house".length)];
      const num = pre.match(/\S+$/)[0];
      return { onChart: true, text: <>{pre.slice(0, -num.length)}<Term k="house">{num} house</Term>{post}</> };
    }
    return MOON_SIGN_TODAY[m.signKey] ? { onChart: false, text: `The Moon is in ${m.sign} today. ${MOON_SIGN_TODAY[m.signKey]}` } : null;
  } catch (e) { return null; }
}

const cap = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const HOUSE_SYSTEM = { placidus: ["placidus", "Placidus"], porphyry: ["porphyry", "Porphyry"], "whole-sign": ["wholeSign", "whole sign"] };

/** A birth within hours of an exact new or full Moon. Before it, the phase
    reading is the one leading up, so name the lunation that was coming. */
function lunationNote(lunation) {
  const exact = lunation.exact;
  if (!exact) return "";
  const moon = exact.kind === "full" ? "full Moon" : "new Moon";
  const h = Math.round(Math.abs(exact.hours));
  const when = h < 1 ? "within an hour of" : `about ${h} hour${h === 1 ? "" : "s"} ${exact.hours < 0 ? "before" : "after"}`;
  const coming = exact.hours < 0 && lunation.key !== exact.kind
    ? (exact.kind === "full" ? ", so its peak colors this phase too" : ", so its fresh start colors this phase too")
    : "";
  return ` You were born ${when} the exact ${moon}${coming}.`;
}

/** Chart-wide facts for the reveal and the Chart tab. Rare, personal facts
    (retrogrades of personal planets, personal stelliums) come before the
    ones everyone has (element, Moon phase). */
export function chartFacts(natal) {
  const P = natal.points;
  const facts = [];
  for (const retro of ["venus", "mars", "mercury"].filter((k) => P[k].retrograde)) {
    facts.push({ key: `retro-${retro}`, eyebrow: <><Term k="retrograde">Retrograde</Term> at birth</>, body: RETRO_READINGS[retro] });
  }
  for (const st of natal.stelliums) {
    const where = st.by === "sign" ? st.sign : `your ${ordinal(st.house)} house (${HOUSE_MEANINGS[st.house].area})`;
    facts.push({
      key: `stellium-${st.by}-${st.sign || st.house}`,
      eyebrow: <>A <Term k="stellium">stellium</Term> in {st.by === "sign" ? st.sign : `the ${ordinal(st.house)} house`}</>,
      body: <>{st.planets.map((k) => PLANET_NAME[k]).join(", ")} all sit in {where}{st.by === "house" ? <>, using <Term k={HOUSE_SYSTEM[natal.houses.system][0]}>{HOUSE_SYSTEM[natal.houses.system][1]}</Term> houses</> : ""}. {st.includesSun
        ? "Your Sun is one of them, so a lot of your energy goes there."
        : "That's a second focus in your chart, next to your Sun sign."}</>,
    });
  }
  if (natal.chartRuler) {
    const r = natal.chartRuler;
    const dig = r.dignity === "domicile" ? <>, its <Term k="domicile">own sign</Term></>
      : r.dignity === "exaltation" ? <>, where it's <Term k="exaltation">exalted</Term></>
      : r.dignity === "detriment" ? <>, its <Term k="detriment">detriment</Term></>
      : r.dignity === "fall" ? <>, its <Term k="fall">fall</Term></>
      : r.dignity === "detriment+fall" ? <>, where it's in both <Term k="detriment+fall">detriment and fall</Term></>
      : "";
    facts.push({
      key: "ruler",
      eyebrow: <>Your <Term k="chartRuler">chart ruler</Term></>,
      body: <>{cap(PLANET_LABEL[r.planet])} rules your {P.asc.sign} Rising{r.modern ? ` (some astrologers say ${PLANET_NAME[r.modern]} too)` : ""}. It sits in {r.sign}{dig}{r.house ? <>, in your {ordinal(r.house)} house ({HOUSE_MEANINGS[r.house].area})</> : ""}{r.retrograde ? <>, and it was <Term k="retrograde">retrograde</Term></> : ""}.{r.house ? " That area of life tends to get a lot of your attention." : ""}{r.cazimi
        ? <> It sat right at the heart of your Sun, which astrologers call <Term k="cazimi">cazimi</Term> and read as a real strength.</>
        : r.combust ? <> It was also within {Math.ceil(r.sunGap)}° of your Sun, which astrologers call <Term k="combust">combust</Term>: its themes can blend into your Sun's and be harder for others to see.</> : ""}</>,
    });
  }
  const { dominantElement, dominantShare, lackingElements } = natal.balance;
  const er = ELEMENT_READINGS[dominantElement];
  facts.push({
    key: "element",
    eyebrow: <>Your <Term k="elements">element</Term> balance</>,
    body: <>{dominantShare >= 0.45 ? er.heavy : er.lead}{lackingElements.length ? ` ${ELEMENT_READINGS[lackingElements[0]].light}` : ""}{natal.moonRange ? ` The Moon changed signs that day, so this counts it in ${natal.points.moon.sign}, where it was at midday, until you add a birth time.` : ""}</>,
  });
  facts.push({
    key: "lunation",
    eyebrow: <>Your birth <Term k="lunation">Moon phase</Term></>,
    body: natal.lunationRange
      ? <>{LUNATION_READINGS[natal.lunationRange.from.key]} The phase changed to {natal.lunationRange.to.name.toLowerCase()} later that day, so a birth time would settle this one too.</>
      : <>{LUNATION_READINGS[natal.lunation.key]}{lunationNote(natal.lunation)}</>,
  });
  return facts;
}

/** Honest notes for untimed births and Rising signs on a sign edge. */
export function uncertaintyNote(natal, body) {
  const r = body === "sun" ? natal.sunRange : natal.moonRange;
  if (r) {
    const name = body === "sun" ? "Sun" : "Moon";
    return `The ${name} moved from ${r.from} into ${r.to}${r.at ? ` at ${r.at} local time` : ""} that day: ${r.from} before, ${r.to} after. This reading uses midday until you add a birth time.`;
  }
  if (body === "rising" && natal.points.asc?.nearCusp) {
    return "Your Rising sign sits right at the edge of a sign, so a few minutes' difference in birth time would change it. Worth double-checking your birth time.";
  }
  return null;
}

const firstPara = (t) => (t || "").split("\n\n")[0];
const NAKSHATRA_OF = Object.fromEntries(NAKSHATRAS.map((n) => [n.name, n]));
const nakshatraNames = (value) => (value || "").split(/, | or /).filter(Boolean);

/** One line for each tradition's card, from the same content its Chart screen uses. */
function lensLines(natal, birthData, facts) {
  const lines = {};
  const sun = natal.points.sun;
  lines.western = natal.sunRange
    ? "The Sun changed signs on the day you were born. A birth time settles which."
    : BIG_THREE.sun?.[sun.signKey]?.line;
  if (facts.vedic) {
    const names = nakshatraNames(facts.vedic.value);
    lines.vedic = names.length > 1
      ? `${names.length === 2 ? "Two" : "Three"} mansions that day. A birth time settles which.`
      : NAKSHATRA_OF[names[0]] && `Your Moon sat in ${names[0]}. ${NAKSHATRA_OF[names[0]].line}`;
  }
  try {
    const c = calculateChineseChart(birthData);
    const w = CHINESE_ANIMAL_WRITEUPS[c.animal.key];
    if (w) lines.chinese = `${w.headline}.`;
  } catch (e) { /* unavailable */ }
  try {
    const m = calculateMayanChart(birthData);
    lines.mayan = MAYAN_DAY_SIGN_WRITEUPS[m.daySign.key]?.essence;
  } catch (e) { /* unavailable */ }
  return lines;
}

/** Moon and Rising under the Western tile's Sun, worded as the share card words them. */
function westernMore(natal) {
  const P = natal.points;
  return [
    natal.moonRange ? `Moon in ${natal.moonRange.from} or ${natal.moonRange.to}` : `Moon in ${P.moon.sign}`,
    P.asc ? `${P.asc.sign} rising` : null,
  ].filter(Boolean);
}

const LensCard = forwardRef(function LensCard({ system, fact, more, line, open, onToggle, delay, wide }, ref) {
  const { key, label, accent } = system;
  const id = `lens-${key}`;
  return (
    <button ref={ref} type="button" className="tw-card-tap tw-rise" onClick={onToggle} aria-expanded={open} aria-controls={open ? "reveal-lens-panel" : undefined}
      aria-labelledby={`${id}-label ${id}-value${more?.length ? ` ${id}-more` : ""}`} aria-describedby={line ? `${id}-line` : undefined} data-lens={key}
      style={{
        animation: `twRise 0.6s ease ${delay}s both`, gridColumn: wide ? "1 / -1" : undefined, display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left",
        gap: 2, padding: "14px 12px 12px", borderRadius: 18, cursor: "pointer", minWidth: 0, color: PD.text,
        // One layer, not three (ba-024): a flat tint, and an accent line only on the open tile.
        border: `1px solid ${open ? accent : "transparent"}`,
        background: `${accent}${open ? "29" : "17"}`,
      }}>
      <span style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
        <NodeGlyph k={key} glyph={fact.glyph} accent={accent} size={40} />
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .25s ease" }}>
          <path d="M3 5l4 4 4-4" stroke={accent} strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </svg>
      </span>
      <span id={`${id}-label`} style={{ ...B, fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", color: accent, marginTop: 8 }}>{label}</span>
      <span id={`${id}-value`} style={{ ...HD, fontSize: fact.value.includes(" or ") ? 15 : 18, fontWeight: 500, lineHeight: 1.2, color: PD.text, overflowWrap: "break-word", hyphens: wide ? "manual" : "auto", maxWidth: "100%" }}>{fact.value}</span>
      <span style={{ ...B, fontSize: 13, color: PD.sub, lineHeight: 1.35, overflowWrap: "break-word", maxWidth: "100%" }}>{fact.sub}</span>
      {more?.length > 0 && (
        <span id={`${id}-more`} style={{ display: "flex", flexDirection: "column", marginTop: 6, gap: 1 }}>
          {more.map((m) => <span key={m} style={{ ...B, fontSize: 14, fontWeight: 500, color: PD.text, lineHeight: 1.35, overflowWrap: "break-word", maxWidth: "100%" }}>{m}</span>)}
        </span>
      )}
      {line && (
        <span id={`${id}-line`} style={{ ...B, fontSize: 13.5, color: PD.text, opacity: 0.88, lineHeight: 1.45, marginTop: 8,
        }}>{line}</span>
      )}
    </button>
  );
});

function PanelText({ children, style }) {
  return <p style={{ ...B, fontSize: 15, color: PD.sub, lineHeight: 1.7, ...style }}>{children}</p>;
}

/** The depth behind each card: what the tradition's own Chart screen opens with. */
function LensPanel({ k, natal, birthData, facts, onAddTime }) {
  const P = natal.points;
  if (k === "western") {
    const chartBits = chartFacts(natal).slice(0, 4);
    return (
      <>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <BigThreeCard role="sun" signKey={P.sun.signKey}
            title={natal.sunRange ? `Sun in ${natal.sunRange.from} or ${natal.sunRange.to}` : `Sun in ${P.sun.sign}`}
            note={uncertaintyNote(natal, "sun")} delay={0} />
          <BigThreeCard role="moon" signKey={P.moon.signKey}
            title={natal.moonRange ? `Moon in ${natal.moonRange.from} or ${natal.moonRange.to}` : `Moon in ${P.moon.sign}`}
            note={uncertaintyNote(natal, "moon")} delay={0.1} />
          {P.asc ? (
            <BigThreeCard role="rising" signKey={P.asc.signKey} title={`${P.asc.sign} Rising`} note={uncertaintyNote(natal, "rising")} delay={0.2} />
          ) : (
            <RisingRow onAddTime={onAddTime} />
          )}
        </div>
        <ChartFactsShort facts={chartBits} />
      </>
    );
  }
  if (k === "vedic") {
    const names = nakshatraNames(facts.vedic.value);
    return (
      <>
        <PanelText>
          Vedic astrology puts a lot of weight on your Moon, and on which of 27 <Term k="nakshatra" accent="#D4A574">Moon mansions</Term> it sat in when you were born.{names.length === 1 && facts.vedic.sub.startsWith("Moon mansion in ") ? ` Your Moon was in ${facts.vedic.sub.slice("Moon mansion in ".length)}, measured against the fixed stars.` : ""}{names.length === 1 && NAKSHATRA_OF[names[0]] ? ` ${names[0]}'s symbol is ${NAKSHATRA_OF[names[0]].symbol}.` : ""}
        </PanelText>
        {/* One mansion: its sentence is already the tile's line, so only the
            two- or three-mansion case gives each one here. */}
        {names.length > 1 && names.map((n) => (
          <p key={n} style={{ ...B, fontSize: 15, color: PD.text, lineHeight: 1.6, marginTop: 12 }}>
            <strong style={{ fontWeight: 600 }}>{n}</strong>{NAKSHATRA_OF[n] ? ` (${NAKSHATRA_OF[n].symbol}). ${NAKSHATRA_OF[n].line}` : ""}
          </p>
        ))}
        {names.length > 1 && (
          <>
            <PanelText style={{ marginTop: 12, fontSize: 14 }}>The Moon moves about one mansion a day, so a birth time settles which one is yours.</PanelText>
            <GhostButton onClick={onAddTime} style={{ paddingLeft: 0, color: "#D4A574" }}>Add birth time</GhostButton>
          </>
        )}
      </>
    );
  }
  if (k === "chinese") {
    const c = calculateChineseChart(birthData);
    const w = CHINESE_ANIMAL_WRITEUPS[c.animal.key];
    // Almanacs count the year from Li Chun (about Feb 4), not Lunar New Year.
    const lc = liChunAnimals(birthData);
    const aAn = (n) => `${/^[AEIOU]/.test(n) ? "an" : "a"} ${n}`;
    const lcNote = lc.length > 1
      ? `You were born on Li Chun day, the start of spring that some almanacs count the year from. By that count, your birth hour decides whether you're ${aAn(lc[0].name)} or ${aAn(lc[1].name)}.`
      : lc[0] && lc[0].key !== c.animal.key
        ? `Lunar New Year and Li Chun, the start of spring that some almanacs count the year from, fell on different sides of your birthday. By that count you'd be ${aAn(lc[0].name)}.`
        : null;
    return (
      <>
        <PanelText>
          Your <Term k="chineseZodiac" accent="#C2625A">year animal</Term> comes from the Chinese year you were born in, and the element changes every two years.
        </PanelText>
        {w && <PanelText style={{ color: PD.text, marginTop: 12 }}>{firstPara(w.body)}</PanelText>}
        {lcNote && <PanelText style={{ marginTop: 12, fontSize: 14 }}>{lcNote}</PanelText>}
      </>
    );
  }
  const m = calculateMayanChart(birthData);
  const ds = MAYAN_DAY_SIGN_WRITEUPS[m.daySign.key];
  const tone = MAYAN_TONE_WRITEUPS[m.toneIdx + 1];
  return (
    <>
      <PanelText>
        The Maya <Term k="tzolkin" accent="#6DA88F">260-day count</Term> gives every day a number and a sign. Yours is {m.toneIdx + 1} {m.daySign.name}.
      </PanelText>
      {ds && <PanelText style={{ color: PD.text, marginTop: 12 }}>{firstPara(ds.body)}</PanelText>}
      {tone && <PanelText style={{ marginTop: 12 }}><strong style={{ fontWeight: 600, color: PD.text }}>Number {m.toneIdx + 1}.</strong> {tone.essence}</PanelText>}
    </>
  );
}

export function Reveal({ birthData, onSave, onEdit, onAddTime, saveLabel = "Save my chart", instant = false }) {
  const [phase, setPhase] = useState(instant ? "reveal" : "reading");
  const [openLens, setOpenLens] = useState(null);
  const panelRef = useRef(null);
  const cardRefs = useRef({});
  const natal = useMemo(() => computeNatal(birthData), [birthData]);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (instant) return undefined;
    const t = setTimeout(() => setPhase("reveal"), reduced ? 300 : 1200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  useEffect(() => { if (phase === "reveal") window.scrollTo(0, 0); }, [phase]);
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (openLens) panelRef.current?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest" });
  }, [openLens]);

  const tiles = useMemo(() => traditionTiles(birthData), [birthData]);
  const facts = useMemo(() => nodeFacts(birthData), [birthData]);
  const lines = useMemo(() => lensLines(natal, birthData, facts), [natal, birthData, facts]);
  const systems = SYSTEMS.filter((s) => facts[s.key]);
  const open = systems.find((s) => s.key === openLens);
  // Two or three mansion names need the full row to keep each name whole.
  // Western spans with it so the other two still pair up, with no gap.
  const wideVedic = nakshatraNames(facts.vedic?.value).length > 1;
  const today = useMemo(() => (phase === "reveal" ? todaySkyLine(natal) : null), [natal, phase]);

  // After the first scroll, Save rides along at the bottom until the save card is on screen.
  const saveCardRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  // Without IntersectionObserver the bar never shows (it could sit over the card).
  const [cardInView, setCardInView] = useState(() => typeof window === "undefined" || !("IntersectionObserver" in window));
  useEffect(() => {
    if (phase !== "reveal") return undefined;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    let io;
    if (saveCardRef.current && "IntersectionObserver" in window) {
      io = new IntersectionObserver(([e]) => setCardInView(e.isIntersecting), { threshold: 0.2 });
      io.observe(saveCardRef.current);
    }
    return () => { window.removeEventListener("scroll", onScroll); io?.disconnect(); };
  }, [phase]);
  const showBar = scrolled && !cardInView;
  // Keyboard focus scrolled into view shouldn't land under the bar.
  useEffect(() => {
    if (!showBar) return undefined;
    document.documentElement.style.scrollPaddingBottom = "96px";
    return () => { document.documentElement.style.scrollPaddingBottom = ""; };
  }, [showBar]);

  return (
    <div style={{ minHeight: "100vh", background: PD.bg, position: "relative", overflowX: "hidden",
      backgroundImage: `radial-gradient(ellipse at 50% 0%, ${SKY.a}26 0%, transparent 60%)` }}>
      <StarField seed={birthData.birthDate.day * 31 + birthData.birthDate.month} />
      <div style={{ position: "relative", zIndex: 1 }}>
        {phase === "reading" ? <Reading birthData={birthData} /> : (
          <div style={{ maxWidth: 520, margin: "0 auto", padding: `8px 16px ${showBar ? 112 : 64}px` }}>
            <BackArrow onClick={onEdit} color={PD.sub} />
            <div className="tw-rise" style={{ textAlign: "center", margin: "4px 0 18px", animation: "twRise 0.6s ease both" }}>
              <h1 style={{ ...HD, fontSize: "clamp(24px, 6.5vw, 30px)", color: PD.text, fontWeight: 500, margin: "0 0 6px", lineHeight: 1.2 }}>
                Your sky, four ways
              </h1>
              <p style={{ ...H, fontSize: 17, fontStyle: "italic", color: PD.sub }}>{birthLine(birthData)}</p>
            </div>

            {/* Two across on a phone; one across when a tile would be too narrow for its words (text zoomed to 200%). */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(max(140px, calc(50% - 5px)), 1fr))", gap: 10 }}>
              {systems.map((s, i) => (
                <LensCard key={s.key} wide={wideVedic && (s.key === "vedic" || s.key === "western")} ref={(el) => { cardRefs.current[s.key] = el; }} system={s} fact={facts[s.key]} more={s.key === "western" ? westernMore(natal) : null}
                  /* Open, Western's panel starts with the same Sun line, so the tile drops it (not the sign-change line, which the panel doesn't repeat). */
                  line={openLens === s.key && s.key === "western" && !natal.sunRange ? null : lines[s.key]} open={openLens === s.key}
                  onToggle={() => setOpenLens(openLens === s.key ? null : s.key)} delay={0.1 + i * 0.08} />
              ))}
            </div>

            <div className="tw-rise" style={{ animation: "twRise 0.6s ease 0.45s both" }}>
              <ShareBigThree natal={natal} birthData={birthData} tiles={tiles} style={{ marginTop: 12 }} />
            </div>

            {open && (
              <section id="reveal-lens-panel" ref={panelRef} aria-label={`${open.label} chart`} key={open.key}
                style={{ marginTop: 12, padding: "18px 16px 8px", borderRadius: 18,
                  background: "rgba(20,22,40,0.72)", animation: "twFade 0.35s ease both", scrollMarginBottom: 96 }}>
                <LensPanel k={open.key} natal={natal} birthData={birthData} facts={facts} onAddTime={onAddTime} />
                <GhostButton onClick={() => { const k = openLens; setOpenLens(null); cardRefs.current[k]?.focus({ preventScroll: false }); }} style={{ paddingLeft: 0, marginTop: 6, color: open.accent }}>Close {open.label}</GhostButton>
              </section>
            )}

            {today && (
              <div className="tw-rise" style={{ marginTop: 18, padding: "0 4px", animation: "twRise 0.6s ease 0.5s both" }}>
                <p style={{ ...B, fontSize: 13, fontWeight: 500, color: SKY.a, marginBottom: 6 }}>{today.onChart ? "Today, on your chart" : "Today's sky"}</p>
                <p style={{ ...H, fontSize: 17, fontStyle: "italic", color: PD.text, lineHeight: 1.5 }}>{today.text}</p>
              </div>
            )}

            <div ref={saveCardRef} className="tw-rise" style={{
              marginTop: 26, padding: "22px 4px 0", textAlign: "center", borderTop: `1px solid ${PD.border}`,
              animation: "twRise 0.6s ease 0.55s both",
            }}>
              <LittleWeb lit={[]} />
              <p style={{ ...HD, fontSize: 19, color: PD.text, fontWeight: 500, marginBottom: 6 }}>Keep this chart</p>
              <p style={{ ...B, fontSize: 14.5, color: PD.sub, lineHeight: 1.55, marginBottom: 16 }}>
                Save it and there's a new read on your chart each morning. Five quick questions come next, for your Web, the side of you the stars don't cover. Skip any you like.
              </p>
              <PrimaryButton onClick={onSave} style={{ width: "100%" }}>{saveLabel}</PrimaryButton>
              <GhostButton onClick={onEdit} style={{ marginTop: 6 }}>Change birth details</GhostButton>
            </div>
            {showBar && (
              <div role="region" aria-label="Save your chart" style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 5, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
                background: `linear-gradient(to top, ${PD.bg} 70%, ${PD.bg}00)`, animation: "twFade 0.25s ease both" }}>
                <div style={{ maxWidth: 488, margin: "0 auto" }}>
                  <PrimaryButton onClick={onSave} style={{ width: "100%" }}>{saveLabel}</PrimaryButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
